import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './NotificationsRegistry.scss';

import { getNotificationRegistry } from '../../api/notifications';
import LabeledSelect from './elements/LabeledSelect';
import InfoTip from './elements/InfoTip';
import HeaderToolbar from './elements/HeaderToolbar';
import EntityAvatar from './elements/EntityAvatar';
import {
  AUDIENCE_LABELS,
  STAGE_LABELS,
  SENT_FROM_CHOICES,
  sentFromChoiceOf,
  CHANNEL_LABELS,
  COPY_STATUS_LABELS,
  TIPS,
  launchStageFor,
  humanizeStage,
  humanizeSentFrom,
  humanizeChannels,
  humanizeCenter,
  gateSummary,
  UNSET_MODE_NOTE
} from '../../helpers/notification_registry_helpers';

const ALL = 'all';
const withAll = options => [{ value: ALL, label: 'All' }, ...options];
const optionsFrom = (values, labels) =>
  _.sortBy(_.uniq(values), value => labels[value] || value).map(value => ({ value, label: labels[value] || value }));

// One row per notification type: its stage and owner beside what code enforces, the state of
// the words, and whether it may leave shadow. Retired rows hide until asked for.
const NotificationsRegistry = ({ onOpenEntry, toolbarSlot = null, initialView = null, onViewChange = null }) => {
  const [rows, setRows] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [filter, setFilter] = useState(initialView?.filter || '');
  const [stage, setStage] = useState(initialView?.stage || ALL);
  const [sentFrom, setSentFrom] = useState(initialView?.sentFrom || ALL);
  const [channel, setChannel] = useState(initialView?.channel || ALL);
  const [audience, setAudience] = useState(initialView?.audience || ALL);
  const [copyStatus, setCopyStatus] = useState(initialView?.copyStatus || ALL);
  const [gate, setGate] = useState(initialView?.gate || ALL);

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);
    getNotificationRegistry()
      .then(resp => !cancelled && setRows((resp.rows || []).map(row => ({ ...row, stage: launchStageFor({ ...row, mode: row.rulebook?.mode }) }))))
      .catch(() => !cancelled && window.ALERT?.error?.('Failed to load the Registry.'))
      .finally(() => !cancelled && setIsFetching(false));
    return () => (cancelled = true);
  }, []);

  useEffect(() => {
    if (onViewChange) onViewChange({ filter, stage, sentFrom, channel, audience, copyStatus, gate });
  }, [onViewChange, filter, stage, sentFrom, channel, audience, copyStatus, gate]);

  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return _.sortBy(
      rows.filter(row => {
        const summary = gateSummary(row);
        return (
          (!q || [row.type, row.name].some(text => text.toLowerCase().includes(q))) &&
          (stage === ALL ? row.stage !== 'retired' : row.stage === stage) &&
          (sentFrom === ALL ||
            (sentFromChoiceOf({ sentFrom: row.sentFrom, pushDelivery: row.rulebook?.pushDelivery }) || 'undecided') === sentFrom) &&
          (channel === ALL || (row.rulebook?.channels || []).includes(channel)) &&
          (audience === ALL || (row.audiences || []).includes(audience)) &&
          (copyStatus === ALL || row.copyStatus === copyStatus) &&
          (gate === ALL || summary.kind === gate)
        );
      }),
      row => row.name.toLowerCase()
    );
  }, [rows, filter, stage, sentFrom, channel, audience, copyStatus, gate]);

  const counts = _.countBy(rows, 'stage');
  const summary = isFetching
    ? 'Loading…'
    : _.compact([
        `${rows.length} notifications`,
        `${counts.live || 0} live`,
        `${counts.shadow || 0} in shadow`,
        counts.allowlist && `${counts.allowlist} on allowlist`,
        `${counts.paused || 0} paused`,
        `${counts.planned || 0} planned`,
        counts.retired && `${counts.retired} retired`
      ]).join(' · ');

  return (
    <div className='notifications-registry-outer'>
      <HeaderToolbar slot={toolbarSlot} className='notifications-registry-outer'>
        <div className='registry-controls'>
          <LabeledSelect
            label='Stage'
            tip={TIPS.stage}
            value={stage}
            onChange={setStage}
            options={withAll(_.map(STAGE_LABELS, (label, value) => ({ value, label })))}
          />
          <LabeledSelect
            label='Sent from'
            tip={TIPS.sentFrom}
            value={sentFrom}
            onChange={setSentFrom}
            options={withAll([...SENT_FROM_CHOICES.map(({ value, label }) => ({ value, label })), { value: 'undecided', label: 'Not decided' }])}
          />
          <LabeledSelect
            label='Channel'
            tip={TIPS.channels}
            value={channel}
            onChange={setChannel}
            options={withAll(optionsFrom(_.uniq(_.flatMap(rows, row => row.rulebook?.channels || [])), CHANNEL_LABELS))}
          />
          <LabeledSelect
            label='Audience'
            tip={TIPS.audience}
            value={audience}
            onChange={setAudience}
            options={withAll(optionsFrom(_.uniq(_.flatMap(rows, row => row.audiences || [])), AUDIENCE_LABELS))}
          />
          <LabeledSelect
            label='Copy'
            tip={TIPS.copyStatus}
            value={copyStatus}
            onChange={setCopyStatus}
            options={withAll(optionsFrom(_.uniq(rows.map(row => row.copyStatus)), COPY_STATUS_LABELS))}
          />
          <LabeledSelect
            label='Gate'
            tip={TIPS.gate}
            value={gate}
            onChange={setGate}
            options={withAll([
              { value: 'blocked', label: 'Blocked' },
              { value: 'ready', label: 'Ready' },
              { value: 'already_live', label: 'Already live' },
              { value: 'ungated', label: 'Ungated' }
            ])}
          />
          <span className='registry-summary'>{summary}</span>
          <input
            className='registry-filter'
            type='text'
            placeholder='Search notifications'
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </div>
      </HeaderToolbar>

      <div className='registry-table'>
        <div className='registry-row head'>
          <span>Notification</span>
          <span>
            Stage
            <InfoTip message={TIPS.stage} />
          </span>
          <span>
            Sent from
            <InfoTip message={TIPS.sentFrom} />
          </span>
          <span>
            Owner
            <InfoTip message={TIPS.owner} />
          </span>
          <span>Area</span>
          <span>
            Channels
            <InfoTip message={TIPS.channels} />
          </span>
          <span>
            Notification Center
            <InfoTip message={TIPS.notificationCenter} />
          </span>
          <span>
            Copy
            <InfoTip message={TIPS.copyStatus} />
          </span>
          <span>
            Gate
            <InfoTip message={TIPS.gate} />
          </span>
        </div>
        {visibleRows.map(row => {
          const gateCell = gateSummary(row);
          return (
            <div key={row.type} className='registry-row' onClick={() => onOpenEntry(row.type)}>
              <span className='entry-name'>
                <span className='name'>{row.name}</span>
                <span className='code'>{row.type}</span>
              </span>
              <span>
                <StageBadge row={row} />
              </span>
              <span className={cn({ muted: !row.sentFrom })}>{humanizeSentFrom(row.sentFrom, { pushDelivery: row.rulebook?.pushDelivery })}</span>
              <span className='owner'>
                {row.owner ? (
                  <>
                    <EntityAvatar className='owner-avatar' name={row.owner.name} image={row.owner.image} />
                    <span>{shortOwnerName(row.owner, visibleRows)}</span>
                  </>
                ) : (
                  <span className='muted'>—</span>
                )}
              </span>
              <span className='areas'>{(row.productAreas || []).join(', ') || '—'}</span>
              <span className={cn({ muted: !humanizeChannels(row.rulebook?.channels) })}>
                {humanizeChannels(row.rulebook?.channels) || '—'}
              </span>
              <span>{humanizeCenter(row.rulebook)}</span>
              <span>
                <span className={cn('badge', `copy-${row.copyStatus}`)}>{COPY_STATUS_LABELS[row.copyStatus] || row.copyStatus}</span>
              </span>
              <span>
                <span className={cn('badge', `gate-${gateCell.kind}`)}>{gateCell.label}</span>
              </span>
            </div>
          );
        })}
        {!isFetching && !visibleRows.length && <div className='registry-empty'>No notifications match</div>}
      </div>
    </div>
  );
};

NotificationsRegistry.propTypes = {
  onOpenEntry: PropTypes.func.isRequired,
  toolbarSlot: PropTypes.instanceOf(Element),
  initialView: PropTypes.object,
  onViewChange: PropTypes.func
};

const StageBadge = ({ row }) => (
  <span
    className={cn('badge', `stage-${row.stage}`)}
    title={row.stage === 'shadow' && !row.rulebook?.onLadder ? UNSET_MODE_NOTE : ''}
  >
    {humanizeStage(row.stage)}
  </span>
);

StageBadge.propTypes = { row: PropTypes.object.isRequired };

// First name alone, with an initial when two owners in view share one.
const shortOwnerName = (owner, rows) => {
  const [first, ...rest] = (owner.name || owner.email || '').split(' ');
  const sharesFirstName = rows.some(row => row.owner && row.owner.id !== owner.id && (row.owner.name || '').split(' ')[0] === first);
  return sharesFirstName && rest.length ? `${first} ${rest[rest.length - 1][0]}.` : first;
};

export default NotificationsRegistry;
